import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format, parseISO, eachDayOfInterval, startOfDay, endOfDay } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  Link as LinkIcon,
  Mail,
  ArrowLeft,
  Edit,
  User,
  Monitor,
  Globe,
  Share2,
  Users as UsersIcon,
  Smartphone,
  Laptop,
  Info,
  Activity,
  UserPlus,
  ChevronUp,
  ChevronDown,
  Search,
  Filter,
  Briefcase,
  DollarSign,
  BarChart2,
  Target,
  Download,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { useState, useMemo } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { utils, writeFile } from 'xlsx';

interface MVPResponse {
  status: string;
  data: {
    _id: string;
    mvpId: string;
    name: string;
    description: string;
    category: string;
    targetAudience: string;
    createdFrom?: string;
    tags: string[];
    status: string;
    notificationEmail?: string;
    owner: string;
    createdAt: string;
    updatedAt: string;
    __v: number;
  };
}

interface Contact {
  _id: string;
  uuid: string;
  mvpId: string;
  user: {
    fullName: string;
    email: string;
    phone: string;
    organization: string;
    role: string;
  };
  intent: {
    subscriptionPlan?: {
      name: string;
      users: number;
      pricePerUser: number;
      estimatedMonthlyIncome: number;
    };
    services?: string[];
    message: string;
  };
  metadata: {
    device: {
      type: string;
      os: string;
      browser: string;
    };
    timestamp: string;
    referrer: string;
    userAgent: string;
    language: string;
    ip: string;
    location?: {
      country: string;
      city: string;
    };
  };
  compliance: {
    terms: boolean;
    privacy: boolean;
    timestamp: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface ContactsResponse {
  status: string;
  data: {
    contacts: Contact[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      pages: number;
    };
  };
}

interface Visit {
  _id: string;
  uuid: string;
  mvpId: string;
  device: {
    type: string;
    os: string;
    browser: string;
  };
  screen: {
    width: number;
    height: number;
  };
  location: {
    country: string;
    region: string;
    city: string;
  };
  utmParams?: {
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
  };
  timestamp: string;
  referrer: string;
  userAgent: string;
  language: string;
  ip: string;
  createdAt: string;
  updatedAt: string;
}

interface VisitsResponse {
  status: string;
  data: {
    visits: Visit[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      pages: number;
    };
    analytics: {
      totalVisits: number;
      deviceTypes: string[];
      countries: string[];
      uniqueVisitors: number;
    };
  };
}


const STATUS_COLORS = {
  'activo': 'bg-emerald-100 text-emerald-800',
  'inactivo': 'bg-gray-100 text-gray-800',
  'borrador': 'bg-amber-100 text-amber-800',
  'archivado': 'bg-red-100 text-red-800'
};

const API_URL = import.meta.env.VITE_API_BASE_URL || '';

type TabType = 'general' | 'visits' | 'contacts';

interface TabProps {
  label: string;
  icon: React.ReactNode;
  count?: number;
}

const TABS: Record<TabType, TabProps> = {
  general: {
    label: 'General',
    icon: <Info className="w-4 h-4" />
  },
  visits: {
    label: 'Visitas',
    icon: <Activity className="w-4 h-4" />
  },
  contacts: {
    label: 'Contactos',
    icon: <UserPlus className="w-4 h-4" />
  }
};

type SortDirection = 'asc' | 'desc';

interface VisitSortConfig {
  key: keyof Visit;
  direction: SortDirection;
}

interface ContactSortConfig {
  key: keyof Contact;
  direction: SortDirection;
}

interface FilterConfig {
  device?: string;
  browser?: string;
  os?: string;
  country?: string;
  utm_source?: string;
}

interface ContactFilterConfig {
  role?: string;
  organization?: string;
  plan?: string;
}

export default function MVPDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('general');
  const [sortConfig, setSortConfig] = useState<VisitSortConfig>({ key: 'createdAt', direction: 'desc' });
  const [contactSortConfig, setContactSortConfig] = useState<ContactSortConfig>({ key: 'createdAt', direction: 'desc' });
  const [filters, setFilters] = useState<FilterConfig>({});
  const [contactFilters, setContactFilters] = useState<ContactFilterConfig>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [contactSearchTerm, setContactSearchTerm] = useState('');
  const [visitsPage, setVisitsPage] = useState(1);
  const [contactsPage, setContactsPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  const { data: response, isLoading, isError } = useQuery<MVPResponse>({
    queryKey: ['mvp', id],
    queryFn: async () => {
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${API_URL}/mvps/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Error fetching MVP details');
      }
      return response.json();
    },
  });

  const { data: contactsResponse, isLoading: isLoadingContacts } = useQuery<ContactsResponse>({
    queryKey: ['contacts', response?.data?.mvpId, contactsPage, ITEMS_PER_PAGE],
    queryFn: async () => {
      const token = sessionStorage.getItem('token');
      const apiResponse = await fetch(`${API_URL}/contacts?mvpId=${response?.data?.mvpId}&page=${contactsPage}&limit=${ITEMS_PER_PAGE}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!apiResponse.ok) {
        throw new Error('Error fetching contacts');
      }
      return apiResponse.json();
    },
    enabled: !!response?.data?.mvpId,
  });

  const { data: visitsResponse, isLoading: isLoadingVisits } = useQuery<VisitsResponse>({
    queryKey: ['visits', response?.data?.mvpId, visitsPage, ITEMS_PER_PAGE],
    queryFn: async () => {
      const token = sessionStorage.getItem('token');
      const apiResponse = await fetch(`${API_URL}/visits?mvpId=${response?.data?.mvpId}&page=${visitsPage}&limit=${ITEMS_PER_PAGE}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!apiResponse.ok) {
        throw new Error('Error fetching visits');
      }
      return apiResponse.json();
    },
    enabled: !!response?.data?.mvpId,
  });

  // Process contacts data for the chart
  const dailyContactsData = useMemo(() => {
    if (!contactsResponse?.data.contacts.length) return [];
    
    // Get date range
    const dates = contactsResponse.data.contacts.map(
      contact => parseISO(contact.createdAt)
    );
    const minDate = new Date(Math.min(...dates.map(d => d.getTime())));
    const maxDate = new Date(Math.max(...dates.map(d => d.getTime())));
    
    // Generate array of all days in range
    const daysInRange = eachDayOfInterval({
      start: minDate,
      end: maxDate
    });
    
    // Count contacts per day
    return daysInRange.map(day => {
      const count = contactsResponse.data.contacts.filter(contact => {
        const contactDate = parseISO(contact.createdAt);
        return contactDate >= startOfDay(day) && contactDate <= endOfDay(day);
      }).length;
      
      return {
        date: format(day, 'dd/MM/yyyy'),
        count
      };
    });
  }, [contactsResponse?.data.contacts]);

  // Process visits data for the chart
  const dailyVisitsData = useMemo(() => {
    if (!visitsResponse?.data.visits.length) return [];
    
    // Get date range
    const dates = visitsResponse.data.visits.map(
      visit => parseISO(visit.createdAt)
    );
    const minDate = new Date(Math.min(...dates.map(d => d.getTime())));
    const maxDate = new Date(Math.max(...dates.map(d => d.getTime())));
    
    // Generate array of all days in range
    const daysInRange = eachDayOfInterval({
      start: minDate,
      end: maxDate
    });
    
    // Count visits and unique visitors per day
    return daysInRange.map(day => {
      const dayVisits = visitsResponse.data.visits.filter(visit => {
        const visitDate = parseISO(visit.createdAt);
        return visitDate >= startOfDay(day) && visitDate <= endOfDay(day);
      });

      // Count unique visitors by IP
      const uniqueIPs = new Set(dayVisits.map(visit => visit.ip));
      
      return {
        date: format(day, 'dd/MM/yyyy'),
        visits: dayVisits.length,
        uniqueVisitors: uniqueIPs.size
      };
    });
  }, [visitsResponse?.data.visits]);

  // Process and filter visits data
  const processedVisits = useMemo(() => {
    if (!visitsResponse?.data.visits) return [];

    let filtered = [...visitsResponse.data.visits];

    // Apply search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(visit => 
        visit.device.type.toLowerCase().includes(searchLower) ||
        visit.device.browser.toLowerCase().includes(searchLower) ||
        visit.device.os.toLowerCase().includes(searchLower) ||
        visit.location.country.toLowerCase().includes(searchLower) ||
        visit.location.city.toLowerCase().includes(searchLower) ||
        (visit.utmParams?.utm_source || '').toLowerCase().includes(searchLower)
      );
    }

    // Apply filters
    if (filters.device) {
      filtered = filtered.filter(visit => visit.device.type === filters.device);
    }
    if (filters.browser) {
      filtered = filtered.filter(visit => visit.device.browser === filters.browser);
    }
    if (filters.os) {
      filtered = filtered.filter(visit => visit.device.os === filters.os);
    }
    if (filters.country) {
      filtered = filtered.filter(visit => visit.location.country === filters.country);
    }
    if (filters.utm_source) {
      filtered = filtered.filter(visit => visit.utmParams?.utm_source === filters.utm_source);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      
      if (aValue === undefined || bValue === undefined) return 0;
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortConfig.direction === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      return sortConfig.direction === 'asc' 
        ? (aValue > bValue ? 1 : -1)
        : (bValue > aValue ? 1 : -1);
    });

    return filtered;
  }, [visitsResponse?.data.visits, sortConfig, filters, searchTerm]);

  // Get unique values for filters
  const filterOptions = useMemo(() => {
    if (!visitsResponse?.data.visits) return {};

    return {
      device: [...new Set(visitsResponse.data.visits.map(v => v.device.type))],
      browser: [...new Set(visitsResponse.data.visits.map(v => v.device.browser))],
      os: [...new Set(visitsResponse.data.visits.map(v => v.device.os))],
      country: [...new Set(visitsResponse.data.visits.map(v => v.location.country))],
      utm_source: [...new Set(visitsResponse.data.visits
        .map(v => v.utmParams?.utm_source)
        .filter((source): source is string => typeof source === 'string'))]
    };
  }, [visitsResponse?.data.visits]);

  // Process contacts data
  const processedContacts = useMemo(() => {
    if (!contactsResponse?.data.contacts) return [];

    let filtered = [...contactsResponse.data.contacts];

    // Apply search term
    if (contactSearchTerm) {
      const searchLower = contactSearchTerm.toLowerCase();
      filtered = filtered.filter(contact => 
        contact.user.fullName.toLowerCase().includes(searchLower) ||
        contact.user.email.toLowerCase().includes(searchLower) ||
        contact.user.organization.toLowerCase().includes(searchLower) ||
        contact.user.role.toLowerCase().includes(searchLower) ||
        (contact.intent.subscriptionPlan?.name || '').toLowerCase().includes(searchLower) ||
        (contact.intent.services || []).some(service => service.toLowerCase().includes(searchLower))
      );
    }

    // Apply filters
    if (contactFilters.role) {
      filtered = filtered.filter(contact => contact.user.role === contactFilters.role);
    }
    if (contactFilters.organization) {
      filtered = filtered.filter(contact => contact.user.organization === contactFilters.organization);
    }
    if (contactFilters.plan) {
      filtered = filtered.filter(contact => 
        contact.intent.subscriptionPlan?.name === contactFilters.plan ||
        (contact.intent.services || []).includes(contactFilters.plan!)
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      const aValue = a[contactSortConfig.key];
      const bValue = b[contactSortConfig.key];
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return contactSortConfig.direction === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      return contactSortConfig.direction === 'asc' 
        ? (aValue > bValue ? 1 : -1)
        : (bValue > aValue ? 1 : -1);
    });

    return filtered;
  }, [contactsResponse?.data.contacts, contactSortConfig, contactFilters, contactSearchTerm]);

  // Get unique values for contact filters
  const contactFilterOptions = useMemo(() => {
    if (!contactsResponse?.data.contacts) return {};

    const plans = contactsResponse.data.contacts
      .map(c => c.intent.subscriptionPlan?.name)
      .filter((plan): plan is string => typeof plan === 'string');
    
    const services = contactsResponse.data.contacts
      .flatMap(c => c.intent.services || [])
      .filter((service): service is string => typeof service === 'string');

    return {
      role: [...new Set(contactsResponse.data.contacts.map(c => c.user.role))],
      organization: [...new Set(contactsResponse.data.contacts.map(c => c.user.organization))],
      plan: [...new Set([...plans, ...services])]
    };
  }, [contactsResponse?.data.contacts]);

  const handleExportVisitsToExcel = () => {
    if (!visitsResponse?.data.visits) return;

    // Preparar los datos para Excel
    const excelData = visitsResponse.data.visits.map(visit => ({
      'Fecha': format(new Date(visit.createdAt), 'dd/MM/yyyy HH:mm:ss'),
      'Tipo de Dispositivo': visit.device.type,
      'Sistema Operativo': visit.device.os,
      'Navegador': visit.device.browser,
      'Resolución': `${visit.screen.width}x${visit.screen.height}`,
      'País': visit.location.country,
      'Región': visit.location.region,
      'Ciudad': visit.location.city,
      'Idioma': visit.language,
      'IP': visit.ip,
      'Referencia': visit.referrer,
      'UTM Source': visit.utmParams?.utm_source || '',
      'UTM Medium': visit.utmParams?.utm_medium || '',
      'UTM Campaign': visit.utmParams?.utm_campaign || ''
    }));

    // Crear el libro de Excel
    const wb = utils.book_new();
    const ws = utils.json_to_sheet(excelData);

    // Ajustar anchos de columna
    const colWidths = [
      { wch: 20 }, // Fecha
      { wch: 15 }, // Tipo de Dispositivo
      { wch: 15 }, // Sistema Operativo
      { wch: 15 }, // Navegador
      { wch: 12 }, // Resolución
      { wch: 15 }, // País
      { wch: 20 }, // Región
      { wch: 20 }, // Ciudad
      { wch: 10 }, // Idioma
      { wch: 15 }, // IP
      { wch: 30 }, // Referencia
      { wch: 15 }, // UTM Source
      { wch: 15 }, // UTM Medium
      { wch: 15 }  // UTM Campaign
    ];
    ws['!cols'] = colWidths;

    utils.book_append_sheet(wb, ws, 'Visitas');
    writeFile(wb, `visitas_${mvp.name}_${format(new Date(), 'dd-MM-yyyy')}.xlsx`);
  };

  const handleExportContactsToExcel = () => {
    if (!contactsResponse?.data.contacts) return;

    // Preparar los datos para Excel
    const excelData = contactsResponse.data.contacts.map(contact => ({
      'Fecha': format(new Date(contact.createdAt), 'dd/MM/yyyy HH:mm:ss'),
      'Nombre': contact.user.fullName,
      'Email': contact.user.email,
      'Teléfono': contact.user.phone || '',
      'Organización': contact.user.organization,
      'Cargo': contact.user.role,
      'Plan/Servicios': contact.intent.subscriptionPlan?.name || (contact.intent.services || []).join(', '),
      'Usuarios': contact.intent.subscriptionPlan?.users || 0,
      'Ingreso Mensual Estimado': contact.intent.subscriptionPlan?.estimatedMonthlyIncome || 0,
      'Mensaje': contact.intent.message || '',
      'Dispositivo': contact.metadata.device.type,
      'Sistema Operativo': contact.metadata.device.os,
      'Navegador': contact.metadata.device.browser,
      'País': contact.metadata.location?.country || '',
      'Ciudad': contact.metadata.location?.city || '',
      'Referencia': contact.metadata.referrer
    }));

    // Crear el libro de Excel
    const wb = utils.book_new();
    const ws = utils.json_to_sheet(excelData);

    // Ajustar anchos de columna
    const colWidths = [
      { wch: 20 }, // Fecha
      { wch: 25 }, // Nombre
      { wch: 30 }, // Email
      { wch: 15 }, // Teléfono
      { wch: 25 }, // Organización
      { wch: 20 }, // Cargo
      { wch: 20 }, // Plan
      { wch: 10 }, // Usuarios
      { wch: 15 }, // Ingreso Mensual
      { wch: 40 }, // Mensaje
      { wch: 15 }, // Dispositivo
      { wch: 15 }, // Sistema Operativo
      { wch: 15 }, // Navegador
      { wch: 15 }, // País
      { wch: 20 }, // Ciudad
      { wch: 30 }  // Referencia
    ];
    ws['!cols'] = colWidths;

    utils.book_append_sheet(wb, ws, 'Contactos');
    writeFile(wb, `contactos_${mvp.name}_${format(new Date(), 'dd-MM-yyyy')}.xlsx`);
  };

  // Función para renderizar la paginación
  const renderPagination = (currentPage: number, totalPages: number, onPageChange: (page: number) => void) => {
    return (
      <div className="flex items-center justify-between border-t border-slate-200 bg-white px-4 py-3 sm:px-6">
        <div className="flex flex-1 justify-between sm:hidden">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`relative inline-flex items-center rounded-md px-4 py-2 text-sm font-medium ${
              currentPage === 1
                ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                : 'bg-white text-primary-600 hover:bg-primary-50'
            }`}
          >
            Anterior
          </button>
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`relative ml-3 inline-flex items-center rounded-md px-4 py-2 text-sm font-medium ${
              currentPage === totalPages
                ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                : 'bg-white text-primary-600 hover:bg-primary-50'
            }`}
          >
            Siguiente
          </button>
        </div>
        <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-slate-700">
              Mostrando página <span className="font-medium">{currentPage}</span> de{' '}
              <span className="font-medium">{totalPages}</span>
            </p>
          </div>
          <div>
            <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
              <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`relative inline-flex items-center rounded-l-md px-2 py-2 text-sm font-medium ${
                  currentPage === 1
                    ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                    : 'bg-white text-primary-600 hover:bg-primary-50'
                }`}
              >
                <span className="sr-only">Anterior</span>
                <ChevronLeft className="h-5 w-5" />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => onPageChange(page)}
                  className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${
                    page === currentPage
                      ? 'z-10 bg-primary-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600'
                      : 'bg-white text-slate-900 hover:bg-primary-50'
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`relative inline-flex items-center rounded-r-md px-2 py-2 text-sm font-medium ${
                  currentPage === totalPages
                    ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                    : 'bg-white text-primary-600 hover:bg-primary-50'
                }`}
              >
                <span className="sr-only">Siguiente</span>
                <ChevronRight className="h-5 w-5" />
              </button>
            </nav>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (isError) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center h-64">
          <p className="text-red-600 mb-4">Error al cargar los detalles del MVP</p>
          <button
            onClick={() => navigate('/mvps')}
            className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700"
          >
            <ArrowLeft className="w-5 h-5" />
            Volver a la lista de MVPs
          </button>
        </div>
      </DashboardLayout>
    );
  }

  if (!response || !response.data) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center h-64">
          <p className="text-slate-600 mb-4">MVP no encontrado</p>
          <button
            onClick={() => navigate('/mvps')}
            className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700"
          >
            <ArrowLeft className="w-5 h-5" />
            Volver a la lista de MVPs
          </button>
        </div>
      </DashboardLayout>
    );
  }

  const mvp = response.data;

  // Update the tab counts
  TABS.visits.count = visitsResponse?.data.pagination.total || 0;
  TABS.contacts.count = contactsResponse?.data.pagination.total || 0;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/mvps')}
              className="inline-flex items-center gap-2 text-slate-600 hover:text-primary-600"
            >
              <ArrowLeft className="w-5 h-5" />
              Volver
            </button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{mvp.name}</h1>
              <p className="mt-1 text-sm text-slate-500">
                {mvp.mvpId}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(`/mvps/${mvp._id}/edit`)}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
            >
              <Edit className="w-4 h-4" />
              Editar
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-200">
          <nav className="flex space-x-8" aria-label="Tabs">
            {(Object.entries(TABS) as [TabType, TabProps][]).map(([key, tab]) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`
                  flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm
                  ${activeTab === key
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                  }
                `}
              >
                {tab.icon}
                {tab.label}
                {tab.count !== undefined && (
                  <span className={`
                    ml-2 py-0.5 px-2 rounded-full text-xs
                    ${activeTab === key
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-slate-100 text-slate-600'
                    }
                  `}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {activeTab === 'general' && (
            <>
              {/* General Info */}
              <div className="bg-white rounded-lg shadow-sm border border-slate-200">
                <div className="p-6">
                  <h2 className="text-lg font-semibold text-slate-900 mb-4">
                    Información General
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-sm font-medium text-slate-600">Descripción</h3>
                      <p className="mt-1 text-slate-900">{mvp.description}</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-slate-600">Público Objetivo</h3>
                      <p className="mt-1 text-slate-900">{mvp.targetAudience}</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-slate-600">Categoría</h3>
                      <p className="mt-1 text-slate-900">{mvp.category}</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-slate-600">Estado</h3>
                      <span className={`
                        inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-1
                        ${STATUS_COLORS[mvp.status as keyof typeof STATUS_COLORS]}
                      `}>
                        {mvp.status}
                      </span>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-slate-600">Etiquetas</h3>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {mvp.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                    {mvp.createdFrom && (
                      <div>
                        <h3 className="text-sm font-medium text-slate-600">URL de Origen</h3>
                        <a
                          href={mvp.createdFrom}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-1 inline-flex items-center gap-1 text-primary-600 hover:text-primary-700"
                        >
                          <LinkIcon className="w-4 h-4" />
                          {mvp.createdFrom}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Analytics Overview */}
              <div className="bg-white rounded-lg shadow-sm border border-slate-200">
                <div className="p-6">
                  <h2 className="text-lg font-semibold text-slate-900 mb-4">
                    Resumen de Analíticas
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-slate-50 rounded-lg p-4">
                      <div className="flex items-center gap-2 text-sm text-slate-600 mb-1">
                        <Monitor className="w-4 h-4" />
                        <span>Visitas Totales</span>
                      </div>
                      <p className="text-2xl font-semibold text-slate-900">
                        {visitsResponse?.data.analytics.totalVisits || 0}
                      </p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-4">
                      <div className="flex items-center gap-2 text-sm text-slate-600 mb-1">
                        <UsersIcon className="w-4 h-4" />
                        <span>Visitantes Únicos</span>
                      </div>
                      <p className="text-2xl font-semibold text-slate-900">
                        {visitsResponse?.data.analytics.uniqueVisitors || 0}
                      </p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-4">
                      <div className="flex items-center gap-2 text-sm text-slate-600 mb-1">
                        <Globe className="w-4 h-4" />
                        <span>Países</span>
                      </div>
                      <p className="text-2xl font-semibold text-slate-900">
                        {visitsResponse?.data.analytics.countries.length || 0}
                      </p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-4">
                      <div className="flex items-center gap-2 text-sm text-slate-600 mb-1">
                        <Share2 className="w-4 h-4" />
                        <span>Tasa de Conversión</span>
                      </div>
                      <p className="text-2xl font-semibold text-slate-900">
                        {visitsResponse?.data.analytics.totalVisits && contactsResponse?.data.pagination.total
                          ? ((contactsResponse.data.pagination.total / visitsResponse.data.analytics.totalVisits) * 100).toFixed(1)
                          : 0}%
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Info */}
              <div className="bg-white rounded-lg shadow-sm border border-slate-200">
                <div className="p-6">
                  <h2 className="text-lg font-semibold text-slate-900 mb-4">
                    Información Adicional
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-sm font-medium text-slate-600">Creado el</h3>
                      <p className="mt-1 text-slate-900">
                        {format(new Date(mvp.createdAt), 'dd MMM yyyy', { locale: es })}
                      </p>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-slate-600">Última actualización</h3>
                      <p className="mt-1 text-slate-900">
                        {format(new Date(mvp.updatedAt), 'dd MMM yyyy', { locale: es })}
                      </p>
                    </div>
                    {mvp.notificationEmail && (
                      <div>
                        <h3 className="text-sm font-medium text-slate-600">Email de notificaciones</h3>
                        <div className="mt-1 flex items-center gap-2 text-slate-900">
                          <Mail className="w-4 h-4 text-slate-400" />
                          <a
                            href={`mailto:${mvp.notificationEmail}`}
                            className="text-primary-600 hover:text-primary-700"
                          >
                            {mvp.notificationEmail}
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          {activeTab === 'visits' && (
            <>
              {/* Visits Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Total Visits */}
                <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-600">Total Visitas</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {visitsResponse?.data.analytics.totalVisits.toLocaleString() || 0}
                      </p>
                    </div>
                    <div className="p-2 bg-blue-50 rounded-lg">
                      <Monitor className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                </div>

                {/* Unique Visitors */}
                <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-600">Visitantes Únicos</p>
                      <p className="text-2xl font-bold text-emerald-600">
                        {visitsResponse?.data.analytics.uniqueVisitors.toLocaleString() || 0}
                      </p>
                    </div>
                    <div className="p-2 bg-emerald-50 rounded-lg">
                      <UsersIcon className="w-6 h-6 text-emerald-600" />
                    </div>
                  </div>
                </div>

                {/* Average Time */}
                <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-600">Países Alcanzados</p>
                      <p className="text-2xl font-bold text-purple-600">
                        {visitsResponse?.data.analytics.countries.length || 0}
                      </p>
                    </div>
                    <div className="p-2 bg-purple-50 rounded-lg">
                      <Globe className="w-6 h-6 text-purple-600" />
                    </div>
                  </div>
                </div>

                {/* Bounce Rate */}
                <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-600">Tasa de Conversión</p>
                      <p className="text-2xl font-bold text-amber-600">
                        {visitsResponse?.data.analytics.totalVisits && contactsResponse?.data.pagination.total
                          ? ((contactsResponse.data.pagination.total / visitsResponse.data.analytics.totalVisits) * 100).toFixed(1)
                          : 0}%
                      </p>
                    </div>
                    <div className="p-2 bg-amber-50 rounded-lg">
                      <BarChart2 className="w-6 h-6 text-amber-600" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Historical Chart */}
              <div className="bg-white rounded-lg shadow-sm border border-slate-200">
                <div className="p-6">
                  <h2 className="text-lg font-semibold text-slate-900 mb-4">
                    Histórico de Visitas
                  </h2>
                  
                  {isLoadingVisits ? (
                    <div className="flex items-center justify-center h-64">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                    </div>
                  ) : visitsResponse?.data.visits.length ? (
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart
                          data={dailyVisitsData}
                          margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" vertical={false} />
                          <XAxis
                            dataKey="date"
                            tick={{ fill: '#64748b' }}
                            tickLine={{ stroke: '#cbd5e1' }}
                            axisLine={{ stroke: '#cbd5e1' }}
                          />
                          <YAxis
                            tick={{ fill: '#64748b' }}
                            tickLine={{ stroke: '#cbd5e1' }}
                            axisLine={{ stroke: '#cbd5e1' }}
                            allowDecimals={false}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: '#ffffff',
                              border: '1px solid #e2e8f0',
                              borderRadius: '0.5rem',
                              boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)'
                            }}
                            labelStyle={{ color: '#0f172a', fontWeight: 500 }}
                            itemStyle={{ color: '#2563eb' }}
                            formatter={(value: number, name: string) => {
                              const label = name === 'visits' ? 'Visitas' : 'Visitantes únicos';
                              return [`${value} ${label.toLowerCase()}`, label];
                            }}
                            labelFormatter={(label: string) => `Fecha: ${label}`}
                          />
                          <Area
                            type="monotone"
                            dataKey="visits"
                            name="Visitas"
                            stackId="1"
                            stroke="#2563eb"
                            fill="#dbeafe"
                            strokeWidth={2}
                          />
                          <Area
                            type="monotone"
                            dataKey="uniqueVisitors"
                            name="Visitantes únicos"
                            stackId="2"
                            stroke="#0f766e"
                            fill="#ccfbf1"
                            strokeWidth={2}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      No hay datos suficientes para mostrar el gráfico
                    </div>
                  )}
                </div>
              </div>

              {/* Visits List */}
              <div className="bg-white rounded-lg shadow-sm border border-slate-200">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-slate-900">
                      Lista de Visitas
                    </h2>
                    <div className="flex items-center gap-4">
                      <button
                        onClick={handleExportVisitsToExcel}
                        disabled={!visitsResponse?.data.visits?.length}
                        className={`
                          inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg
                          ${!visitsResponse?.data.visits?.length
                            ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                            : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                          }
                        `}
                      >
                        <Download className="w-4 h-4" />
                        Exportar a Excel
                      </button>
                      <div className="relative">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                        <input
                          type="text"
                          placeholder="Buscar visitas..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                      <button
                        onClick={() => setFilters({})}
                        className="text-sm text-slate-600 hover:text-primary-600"
                      >
                        Limpiar filtros
                      </button>
                    </div>
                  </div>

                  {/* Filters */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                    {Object.entries(filterOptions).map(([key, values]) => (
                      <div key={key} className="relative">
                        <select
                          value={filters[key as keyof FilterConfig] || ''}
                          onChange={(e) => setFilters(prev => ({
                            ...prev,
                            [key]: e.target.value || undefined
                          }))}
                          className="w-full pl-3 pr-10 py-2 text-sm border border-slate-200 rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        >
                          <option value="">Filtrar por {key}</option>
                          {values.map((value: string) => (
                            <option key={value} value={value}>{value}</option>
                          ))}
                        </select>
                        <Filter className="w-4 h-4 absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 pointer-events-none" />
                      </div>
                    ))}
                  </div>

                  {/* Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                            <button
                              className="flex items-center gap-1"
                              onClick={() => setSortConfig({
                                key: 'createdAt',
                                direction: sortConfig.key === 'createdAt' && sortConfig.direction === 'asc' ? 'desc' : 'asc'
                              })}
                            >
                              Fecha
                              {sortConfig.key === 'createdAt' && (
                                sortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                              )}
                            </button>
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Dispositivo</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Ubicación</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Referencia</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">UTM</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {processedVisits.map((visit) => (
                          <tr key={visit._id} className="hover:bg-slate-50">
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-900">
                              <div>{format(new Date(visit.createdAt), 'dd MMM yyyy', { locale: es })}</div>
                              <div className="text-slate-500">{format(new Date(visit.createdAt), 'HH:mm:ss')}</div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm">
                              <div className="flex items-center gap-2">
                                {visit.device.type === 'desktop' ? (
                                  <Laptop className="w-4 h-4 text-slate-400" />
                                ) : (
                                  <Smartphone className="w-4 h-4 text-slate-400" />
                                )}
                                <div>
                                  <div className="text-slate-900">{visit.device.type}</div>
                                  <div className="text-slate-500">
                                    {visit.device.browser} • {visit.device.os}
                                  </div>
                                  <div className="text-slate-500">
                                    {visit.screen.width}x{visit.screen.height}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm">
                              <div className="flex items-start gap-2">
                                <Globe className="w-4 h-4 text-slate-400 mt-1" />
                                <div>
                                  <div className="text-slate-900">{visit.location.city}, {visit.location.country}</div>
                                  <div className="text-slate-500">{visit.location.region}</div>
                                  <div className="text-slate-500">{visit.language}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm">
                              <div className="max-w-xs truncate text-slate-900">
                                {visit.referrer ? new URL(visit.referrer).hostname : 'Acceso directo'}
                              </div>
                              <div className="text-slate-500">
                                IP: {visit.ip}
                              </div>
                            </td>
                            <td className="px-4 py-4 text-sm">
                              {(visit.utmParams?.utm_source || visit.utmParams?.utm_medium || visit.utmParams?.utm_campaign) ? (
                                <div className="space-y-1">
                                  {visit.utmParams?.utm_source && (
                                    <div className="inline-flex items-center px-2 py-1 rounded-md bg-slate-100 text-xs text-slate-700">
                                      source: {visit.utmParams && visit.utmParams.utm_source}
                                    </div>
                                  )}
                                  {visit.utmParams?.utm_medium && (
                                    <div className="inline-flex items-center px-2 py-1 rounded-md bg-slate-100 text-xs text-slate-700">
                                      medium: {visit.utmParams && visit.utmParams.utm_medium}
                                    </div>
                                  )}
                                  {visit.utmParams?.utm_campaign && (
                                    <div className="inline-flex items-center px-2 py-1 rounded-md bg-slate-100 text-xs text-slate-700">
                                      campaign: {visit.utmParams && visit.utmParams.utm_campaign}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <span className="text-slate-500">Sin parámetros UTM</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {processedVisits.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        No se encontraron visitas con los filtros aplicados
                      </div>
                    ) : (
                      visitsResponse?.data.pagination && (
                        renderPagination(
                          visitsPage,
                          visitsResponse.data.pagination.pages,
                          setVisitsPage
                        )
                      )
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          {activeTab === 'contacts' && (
            <>
              {/* Contacts Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Total Contacts */}
                <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-600">Total Contactos</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {contactsResponse?.data.pagination.total.toLocaleString() || 0}
                      </p>
                    </div>
                    <div className="p-2 bg-primary-50 rounded-lg">
                      <UserPlus className="w-6 h-6 text-primary-600" />
                    </div>
                  </div>
                </div>

                {/* Total Revenue */}
                <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-600">Ingresos Potenciales</p>
                      <p className="text-2xl font-bold text-emerald-600">
                        ${contactsResponse?.data.contacts.reduce((sum, contact) => 
                          sum + (contact.intent.subscriptionPlan?.estimatedMonthlyIncome || 0), 0).toLocaleString() || 0}
                      </p>
                    </div>
                    <div className="p-2 bg-emerald-50 rounded-lg">
                      <DollarSign className="w-6 h-6 text-emerald-600" />
                    </div>
                  </div>
                </div>

                {/* Average Deal Size */}
                <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-600">Promedio por Contacto</p>
                      <p className="text-2xl font-bold text-purple-600">
                        ${contactsResponse?.data.contacts.length 
                          ? Math.round(contactsResponse.data.contacts.reduce((sum, contact) => 
                              sum + (contact.intent.subscriptionPlan?.estimatedMonthlyIncome || 0), 0
                            ) / contactsResponse.data.contacts.length).toLocaleString()
                          : 0}
                      </p>
                    </div>
                    <div className="p-2 bg-purple-50 rounded-lg">
                      <Target className="w-6 h-6 text-purple-600" />
                    </div>
                  </div>
                </div>

                {/* Organizations */}
                <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-600">Organizaciones</p>
                      <p className="text-2xl font-bold text-amber-600">
                        {new Set(contactsResponse?.data.contacts.map(contact => 
                          contact.user.organization)).size || 0}
                      </p>
                    </div>
                    <div className="p-2 bg-amber-50 rounded-lg">
                      <Briefcase className="w-6 h-6 text-amber-600" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Historical Chart */}
              <div className="bg-white rounded-lg shadow-sm border border-slate-200">
                <div className="p-6">
                  <h2 className="text-lg font-semibold text-slate-900 mb-4">
                    Histórico de Contactos
                  </h2>
                  
                  {isLoadingContacts ? (
                    <div className="flex items-center justify-center h-64">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                    </div>
                  ) : contactsResponse?.data.contacts.length ? (
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart
                          data={dailyContactsData}
                          margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" vertical={false} />
                          <XAxis
                            dataKey="date"
                            tick={{ fill: '#64748b' }}
                            tickLine={{ stroke: '#cbd5e1' }}
                            axisLine={{ stroke: '#cbd5e1' }}
                          />
                          <YAxis
                            tick={{ fill: '#64748b' }}
                            tickLine={{ stroke: '#cbd5e1' }}
                            axisLine={{ stroke: '#cbd5e1' }}
                            allowDecimals={false}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: '#ffffff',
                              border: '1px solid #e2e8f0',
                              borderRadius: '0.5rem',
                              boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)'
                            }}
                            labelStyle={{ color: '#0f172a', fontWeight: 500 }}
                            itemStyle={{ color: '#2563eb' }}
                            formatter={(value: number) => [`${value} contactos`, 'Contactos']}
                            labelFormatter={(label: string) => `Fecha: ${label}`}
                          />
                          <Area
                            type="monotone"
                            dataKey="count"
                            stroke="#2563eb"
                            fill="#dbeafe"
                            strokeWidth={2}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      No hay datos suficientes para mostrar el gráfico
                    </div>
                  )}
                </div>
              </div>

              {/* Contacts List */}
              <div className="bg-white rounded-lg shadow-sm border border-slate-200">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-slate-900">
                      Lista de Contactos
                    </h2>
                    <div className="flex items-center gap-4">
                      <button
                        onClick={handleExportContactsToExcel}
                        disabled={!contactsResponse?.data.contacts?.length}
                        className={`
                          inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg
                          ${!contactsResponse?.data.contacts?.length
                            ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                            : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                          }
                        `}
                      >
                        <Download className="w-4 h-4" />
                        Exportar a Excel
                      </button>
                      <div className="relative">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                        <input
                          type="text"
                          placeholder="Buscar contactos..."
                          value={contactSearchTerm}
                          onChange={(e) => setContactSearchTerm(e.target.value)}
                          className="pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                      <button
                        onClick={() => setContactFilters({})}
                        className="text-sm text-slate-600 hover:text-primary-600"
                      >
                        Limpiar filtros
                      </button>
                    </div>
                  </div>

                  {/* Filters */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                    {Object.entries(contactFilterOptions).map(([key, values]) => (
                      <div key={key} className="relative">
                        <select
                          value={contactFilters[key as keyof ContactFilterConfig] || ''}
                          onChange={(e) => setContactFilters(prev => ({
                            ...prev,
                            [key]: e.target.value || undefined
                          }))}
                          className="w-full pl-3 pr-10 py-2 text-sm border border-slate-200 rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        >
                          <option value="">Filtrar por {key}</option>
                          {values.map((value: string) => (
                            <option key={value} value={value}>{value}</option>
                          ))}
                        </select>
                        <Filter className="w-4 h-4 absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 pointer-events-none" />
                      </div>
                    ))}
                  </div>

                  {/* Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                            <button
                              className="flex items-center gap-1"
                              onClick={() => setContactSortConfig({
                                key: 'createdAt',
                                direction: contactSortConfig.key === 'createdAt' && contactSortConfig.direction === 'asc' ? 'desc' : 'asc'
                              })}
                            >
                              Fecha
                              {contactSortConfig.key === 'createdAt' && (
                                contactSortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                              )}
                            </button>
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Usuario</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Organización</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Plan</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Dispositivo</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {processedContacts.map((contact) => (
                          <tr key={contact._id} className="hover:bg-slate-50">
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-900">
                              <div>{format(new Date(contact.createdAt), 'dd MMM yyyy', { locale: es })}</div>
                              <div className="text-slate-500">{format(new Date(contact.createdAt), 'HH:mm:ss')}</div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm">
                              <div className="flex items-start gap-2">
                                <User className="w-4 h-4 text-slate-400 mt-1" />
                                <div>
                                  <div className="text-slate-900">{contact.user.fullName}</div>
                                  <div className="text-slate-500">{contact.user.email}</div>
                                  {contact.user.phone && (
                                    <div className="text-slate-500">{contact.user.phone}</div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm">
                              <div className="text-slate-900">{contact.user.organization}</div>
                              <div className="text-slate-500">{contact.user.role}</div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm">
                              {contact.intent.subscriptionPlan ? (
                                <>
                                  <div className="text-slate-900">{contact.intent.subscriptionPlan.name}</div>
                                  <div className="text-slate-500">{contact.intent.subscriptionPlan.users} usuarios</div>
                                  <div className="text-slate-500">${contact.intent.subscriptionPlan.estimatedMonthlyIncome}/mes</div>
                                </>
                              ) : (
                                <>
                                  <div className="text-slate-900">Servicios:</div>
                                  <div className="text-slate-500">
                                    {(contact.intent.services || []).map((service, index) => (
                                      <span key={index} className="inline-block bg-slate-100 text-slate-700 px-2 py-1 rounded text-xs mr-1 mb-1">
                                        {service}
                                      </span>
                                    ))}
                                  </div>
                                </>
                              )}
                            </td>
                            <td className="px-4 py-4 text-sm">
                              <div className="space-y-2">
                                <div className="text-slate-900">
                                  {contact.metadata.device.type} • {contact.metadata.device.os}
                                </div>
                                <div className="text-slate-500">
                                  {contact.metadata.device.browser}
                                </div>
                                <div className="text-slate-500">
                                  {contact.metadata.referrer ? new URL(contact.metadata.referrer).hostname : 'Acceso directo'}
                                </div>
                              </div>
                              {contact.intent.message && (
                                <div className="mt-2 pt-2 border-t border-slate-200">
                                  <div className="text-slate-600 text-sm line-clamp-2">
                                    {contact.intent.message}
                                  </div>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {processedContacts.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        No se encontraron contactos con los filtros aplicados
                      </div>
                    ) : (
                      contactsResponse?.data.pagination && (
                        renderPagination(
                          contactsPage,
                          contactsResponse.data.pagination.pages,
                          setContactsPage
                        )
                      )
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}